"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Badge } from "@/src/components/shadcn/ui/badge";
import type { ProgressNoteContext } from "@/src/lib/ehr/referral-matching";
import type { ReferralMatchOutput, ReferralMatchReferralSummary } from "@/src/lib/workflows/steps/step-outputs";
interface ReferralMatchPanelProps {
  match: ReferralMatchOutput | undefined;
}
export function ReferralMatchPanelView(t0) {
  const $ = _c(26);
  const {
    match
  } = t0;
  if (!match) {
    return null;
  }
  if (match.unableToSchedule || match.cannotServicePatient || match.earlySteps) {
    return null;
  }
  if (match.matched && match.matchedReferral) {
    let t1;
    if ($[0] !== match.intakeEvidence) {
      t1 = <IntakeEvidenceSection evidence={match.intakeEvidence} />;
      $[0] = match.intakeEvidence;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p><strong>Matched Referral (verify before completing):</strong></p>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== match.matchedReferral) {
      t3 = <ReferralSummaryRow referral={match.matchedReferral} />;
      $[3] = match.matchedReferral;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <div className="flex flex-col gap-2 mt-2">{t1}{t2}{t3}</div>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (match.matched) {
    return null;
  }
  let t1;
  if ($[8] !== match.candidateReferrals) {
    t1 = match.candidateReferrals ?? [];
    $[8] = match.candidateReferrals;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const candidates = t1;
  if (candidates.length === 0) {
    let t2;
    if ($[10] !== match.intakeEvidence) {
      t2 = <IntakeEvidenceSection evidence={match.intakeEvidence} />;
      $[10] = match.intakeEvidence;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p><strong>Candidate Referrals:</strong></p>;
      t4 = <p className="text-muted-foreground italic">None in the matching date window.</p>;
      $[12] = t3;
      $[13] = t4;
    } else {
      t3 = $[12];
      t4 = $[13];
    }
    let t5;
    if ($[14] !== t2) {
      t5 = <div className="flex flex-col gap-2 mt-2">{t2}{t3}{t4}</div>;
      $[14] = t2;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  let t2;
  if ($[16] !== match.intakeEvidence) {
    t2 = <IntakeEvidenceSection evidence={match.intakeEvidence} />;
    $[16] = match.intakeEvidence;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p><strong>Candidate Referrals (verify none match):</strong></p>;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== candidates) {
    t4 = candidates.map(_temp);
    $[19] = candidates;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t4) {
    t5 = <div className="flex flex-col gap-3">{t4}</div>;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t2 || $[24] !== t5) {
    t6 = <div className="flex flex-col gap-2 mt-2">{t2}{t3}{t5}</div>;
    $[23] = t2;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
function _temp(candidate, idx) {
  return <ReferralSummaryRow key={idx} referral={candidate} index={idx + 1} />;
}
function IntakeEvidenceSection(t0) {
  const $ = _c(30);
  const {
    evidence
  } = t0;
  if (!evidence) {
    return null;
  }
  let t1;
  if ($[0] !== evidence.providerName || $[1] !== evidence.providerNpi) {
    t1 = [evidence.providerName, evidence.providerNpi ? `NPI: ${evidence.providerNpi}` : null].filter(Boolean).join(" \xB7 ") || null;
    $[0] = evidence.providerName;
    $[1] = evidence.providerNpi;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const providerDisplay = t1;
  let t2;
  if ($[3] !== evidence.clinicAddress || $[4] !== evidence.clinicName) {
    t2 = [evidence.clinicName, evidence.clinicAddress].filter(Boolean).join(", ") || null;
    $[3] = evidence.clinicAddress;
    $[4] = evidence.clinicName;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      label: "Location",
      value: t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== providerDisplay) {
    t4 = {
      label: "Provider",
      value: providerDisplay
    };
    $[8] = providerDisplay;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== evidence.specialty) {
    t5 = {
      label: "Specialty",
      value: evidence.specialty
    };
    $[10] = evidence.specialty;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== evidence.dateOfEvent) {
    t6 = {
      label: "Date of event",
      value: evidence.dateOfEvent
    };
    $[12] = evidence.dateOfEvent;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== evidence.reasonForVisit) {
    t7 = {
      label: "Reason for visit",
      value: evidence.reasonForVisit
    };
    $[14] = evidence.reasonForVisit;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== evidence.diagnosisCodes) {
    t8 = (evidence.diagnosisCodes?.length ?? 0) > 0 ? evidence.diagnosisCodes.join(", ") : null;
    $[16] = evidence.diagnosisCodes;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = {
      label: "ICD-10 codes",
      value: t8
    };
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t9) {
    const lines = [t3, t4, t5, t6, t7, t9];
    t10 = lines.filter(_temp2);
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const visibleLines = t10;
  if (visibleLines.length === 0) {
    return null;
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p><strong>Intake:</strong></p>;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== visibleLines) {
    t12 = <div className="flex flex-col gap-1 mb-1">{t11}<div className="pl-4 border-l-2 border-blue-500/30">{visibleLines.map(_temp3)}</div></div>;
    $[28] = visibleLines;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp3(t0) {
  const {
    label,
    value: value_0
  } = t0;
  return <p key={label} className="whitespace-pre-line"><strong>{label}:</strong> {value_0}</p>;
}
function _temp2(t0) {
  const {
    value
  } = t0;
  return value;
}
function ReferralSummaryRow(t0) {
  const $ = _c(41);
  const {
    referral,
    index
  } = t0;
  let t1;
  if ($[0] !== referral.performerName) {
    t1 = {
      label: "Facility",
      value: referral.performerName
    };
    $[0] = referral.performerName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== referral.performerAddress) {
    t2 = {
      label: "Facility address",
      value: referral.performerAddress
    };
    $[2] = referral.performerAddress;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== referral.requesterName) {
    t3 = {
      label: "Ordering provider",
      value: referral.requesterName
    };
    $[4] = referral.requesterName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== referral.specialty) {
    t4 = {
      label: "Specialty",
      value: referral.specialty
    };
    $[6] = referral.specialty;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== referral.reasonText) {
    t5 = {
      label: "Reason",
      value: referral.reasonText
    };
    $[8] = referral.reasonText;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== referral.icdCodes) {
    t6 = referral.icdCodes.length > 0 ? referral.icdCodes.join(", ") : null;
    $[10] = referral.icdCodes;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = {
      label: "ICD codes",
      value: t6
    };
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== referral.cptCodes) {
    t8 = referral.cptCodes.length > 0 ? referral.cptCodes.join(", ") : null;
    $[14] = referral.cptCodes;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = {
      label: "CPT codes",
      value: t8
    };
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = referral.startDate || referral.endDate ? `${referral.startDate ?? "?"} → ${referral.endDate ?? "?"}` : null;
  let t11;
  if ($[18] !== t10) {
    t11 = {
      label: "Date range",
      value: t10
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t1 || $[21] !== t11 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t7 || $[27] !== t9) {
    t12 = [t1, t2, t3, t4, t5, t7, t9, t11];
    $[20] = t1;
    $[21] = t11;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const lines = t12;
  let t13;
  if ($[29] !== index || $[30] !== referral.status) {
    t13 = index != null && <p className="flex items-center gap-2"><strong>#{index}</strong>{referral.status && <Badge variant="secondary">{referral.status}</Badge>}</p>;
    $[29] = index;
    $[30] = referral.status;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== index || $[33] !== referral.status) {
    t14 = index == null && referral.status && <p><Badge variant="secondary">{referral.status}</Badge></p>;
    $[32] = index;
    $[33] = referral.status;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== lines) {
    t15 = lines.map(_temp4);
    $[35] = lines;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t14 || $[39] !== t15) {
    t16 = <div className="pl-4 border-l-2 border-green-500/30">{t13}{t14}{t15}</div>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}
function _temp4(t0) {
  const {
    label,
    value
  } = t0;
  return value ? <p key={label} className="whitespace-pre-line"><strong>{label}:</strong> {value}</p> : null;
}